import axios, { AxiosHeaders, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { HttpError } from '@refinedev/core';
import { addHttpBreadcrumb } from '@admin-panel/observability';
import { PORTAL_NAME } from '@admin-panel/config/env';
import { getDeviceFingerprint } from '@admin-panel/utils';
import { buildHeaders, getPortalHeadersByName } from '../headers';
import { handleUnauthorizedSessionExpiry } from '../authSession';

const axiosInstance = axios.create();

export type PortalAxiosRequestConfig = AxiosRequestConfig & {
  skipUnauthorizedLogout?: boolean;
};

type AxiosRequestMetadata = {
  startedAt: number;
  request: {
    url?: string;
    method?: string;
    headers?: unknown;
    body?: unknown;
    params?: unknown;
  };
};

type PortalAxiosRequest = InternalAxiosRequestConfig & PortalAxiosRequestConfig;

const isAuthRequest = (url?: string) => typeof url === 'string' && /\/auth(?:\/|$)/i.test(url);

const getPersistedIdentityTokens = (): { token: string | null; refreshToken: string | null } => {
  const persistedRoot = localStorage.getItem('persist:root');
  if (!persistedRoot) {
    return { token: null, refreshToken: null };
  }

  try {
    const rootState = JSON.parse(persistedRoot) as {
      identity?: string | { token?: string | null; refreshToken?: string | null };
    };

    if (!rootState.identity) {
      return { token: null, refreshToken: null };
    }

    const identityState =
      typeof rootState.identity === 'string' ? JSON.parse(rootState.identity) : rootState.identity;

    return {
      token: identityState?.token ?? null,
      refreshToken: identityState?.refreshToken ?? null,
    };
  } catch (error) {
    console.warn('Could not parse persisted auth state for axios headers', error);
    return { token: null, refreshToken: null };
  }
};

axiosInstance.interceptors.request.use(config => {
  const { token: persistedToken, refreshToken: persistedRefreshToken } =
    getPersistedIdentityTokens();
  const at = localStorage.getItem('at') || persistedToken;
  const rt = localStorage.getItem('rt') || persistedRefreshToken;
  const { portalCode, app } = getPortalHeadersByName(PORTAL_NAME);
  const headers = buildHeaders({
    headers: config.headers,
    accessToken: at,
    refreshToken: rt,
    deviceId: getDeviceFingerprint(),
    portalCode,
    app,
    skipAdminVendorHeader: PORTAL_NAME === 'synq',
  });

  config.headers = AxiosHeaders.from(Object.fromEntries(headers.entries()));
  (config as PortalAxiosRequest & { metadata?: AxiosRequestMetadata }).metadata = {
    startedAt: performance.now(),
    request: {
      url: config.url,
      method: config.method,
      headers: config.headers,
      body: config.data,
      params: config.params,
    },
  };
  return config;
});

axiosInstance.interceptors.response.use(
  response => {
    const metadata = (response.config as typeof response.config & { metadata?: AxiosRequestMetadata })
      .metadata;
    addHttpBreadcrumb({
      transport: 'axios',
      phase: 'success',
      request: metadata?.request ?? {
        url: response.config.url,
        method: response.config.method,
        headers: response.config.headers,
        body: response.config.data,
        params: response.config.params,
      },
      response: {
        status: response.status,
        headers: response.headers,
        body: response.data,
        durationMs: metadata ? Math.round(performance.now() - metadata.startedAt) : undefined,
      },
    });

    // if response is coming from lk backend services
    if (response.data && response.data.status && response.data.status.type !== 'SUCCESS') {
      const customError: HttpError = {
        message: response?.data?.status.message,
        statusCode: response?.data?.status.code,
      };
      return Promise.reject(customError);
    }
    return response;
  },
  error => {
    const metadata = (error.config as (typeof error.config & { metadata?: AxiosRequestMetadata }) | undefined)
      ?.metadata;
    addHttpBreadcrumb({
      transport: 'axios',
      phase: 'error',
      request: metadata?.request ?? {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
        body: error.config?.data,
        params: error.config?.params,
      },
      response: error.response
        ? {
            status: error.response.status,
            headers: error.response.headers,
            body: error.response.data,
            durationMs: metadata ? Math.round(performance.now() - metadata.startedAt) : undefined,
          }
        : undefined,
      error,
    });
    if (error.response == null) {
      const customError: HttpError = {
        ...error,
        message: error.message,
        statusCode: 500,
      };
      return Promise.reject(customError);
    }

    const requestConfig = error.config as (PortalAxiosRequest & { metadata?: AxiosRequestMetadata }) | undefined;

    if (
      error.response.status === 401 &&
      !requestConfig?.skipUnauthorizedLogout &&
      !isAuthRequest(requestConfig?.url)
    ) {
      handleUnauthorizedSessionExpiry();
    }
    const customError: HttpError = {
      ...error,
      message: error.response?.statusText || error.message || 'Request failed',
      statusCode: error.response?.status,
    };
    if (error.response.status === 403) {
      customError.message = 'You are not allowed to access this resource';
    }
    if (error.response.status === 404) {
      customError.message = 'Wrong url configured. Please contact your tech team';
    }
    if (error.response.status === 500) {
      customError.message = 'Something went wrong. Please try again later';
    }
    if (error.response?.data && error.response?.data.status) {
      customError.message = error.response?.data?.status.message;
    }
    return Promise.reject(customError);
  },
);

export { axiosInstance };
